import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { splitText } from 'griffo'

type ScienceAnimationAnalyticsValue = string | number | boolean

type ScienceAnimationAnalyticsParams = Record<
	string,
	ScienceAnimationAnalyticsValue
>

interface KeyframeData {
	element: HTMLElement
	chars: HTMLSpanElement[]
	body: HTMLElement | null
	reverts: (() => void)[]
	start: number
	end: number
	fadeIn: number
	fadeOut: number
	// 0 | 1 = idle at that opacity (no need to re-write styles); null = mid-animation
	charsIdle: 0 | 1 | null
	bodyIdle: 0 | 1 | null
}

function scienceAnimationAnalyticsAllowed() {
	try {
		const privacy = window.Shopify?.customerPrivacy
		if (!privacy) return true
		if (typeof privacy.analyticsProcessingAllowed === 'function') {
			return privacy.analyticsProcessingAllowed()
		}
		if (typeof privacy.userCanBeTracked === 'function') {
			return privacy.userCanBeTracked()
		}
	} catch {
		/* fall through to permissive default */
	}
	return true
}

export class ScienceAnimation extends UcoastEl {
	static htmlSelector = 'science-animation'

	private outer: HTMLElement
	private sticky: HTMLElement
	private canvas?: HTMLCanvasElement
	private ctx?: CanvasRenderingContext2D
	private poster?: HTMLImageElement
	private loader?: HTMLElement
	private loaderProgress?: HTMLElement
	private skipButton?: HTMLButtonElement
	private keyframeData: KeyframeData[] = []
	private reducedMotion: boolean

	private frameUrls: string[] = []
	private frames: (HTMLImageElement | null)[] = []
	private framesLoaded: boolean[] = []
	private framesQueued: boolean[] = []
	private loadQueue: number[] = []
	private inflightLoads = 0
	private loadedCount = 0
	private allFramesReady = false
	private animationReady = false
	private frameLoadStarted = false
	private currentFrameIndex = -1
	private dpr = 1
	private intersected = false
	private loaderShown = false
	private loaderTimeoutId?: number
	private lastScrollY = 0
	private scrollDirection: 'up' | 'down' = 'down'

	private boundScrollHandler?: () => void
	private boundResizeHandler?: () => void
	private intersectionObserver?: IntersectionObserver
	private frameRequestId?: number
	private targetProgress = 0
	private smoothedProgress = 0
	private firstFrameHold = 0
	private lastFrameHold = 0
	private mobileBreakpoint: number
	private sectionId: string
	private pageType: string
	private connectedAt = performance.now()
	private frameLoadStartedAt?: number
	private hasTrackedInit = false
	private hasTrackedView = false
	private hasTrackedReady = false
	private hasTrackedLoaderShown = false
	private hasTrackedFrameError = false

	private readonly smoothingFactor = 0.2
	private readonly smoothingEpsilon = 0.0005
	private readonly charStaggerTotal = 0.4
	private readonly loaderShowDelayMs = 250
	private readonly loadConcurrency = 3
	private readonly preloadRadius = 4
	private readonly frameDecodeTimeoutMs = 150
	private readonly bodyDelay = 0.01
	private readonly bodyFadeDuration = 0.02

	constructor() {
		super()
		this.outer = q.rs('.science-animation__outer', this)
		this.sticky = q.rs('.science-animation__sticky', this)
		this.canvas = q.os<HTMLCanvasElement>('[data-canvas]', this) ?? undefined
		this.poster = q.os<HTMLImageElement>('.science-animation__poster', this) ?? undefined
		this.loader = q.os<HTMLElement>('[data-loader]', this) ?? undefined
		this.loaderProgress =
			q.os<HTMLElement>('[data-loader-progress]', this) ?? undefined
		this.skipButton = q.os<HTMLButtonElement>('[data-skip]', this) ?? undefined
		this.mobileBreakpoint = parseInt(this.dataset.mobileBreakpoint ?? '749', 10)
		this.sectionId = this.dataset.sectionId ?? ''
		this.pageType = this.dataset.pageType ?? ''
		this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

		this.setupKeyframeText()
		this.parseFrameUrls()
		this.parseFrameHoldSettings()
		this.setupCanvas()
		this.setupSkipButton()

		if (this.reducedMotion) {
			this.applyReducedMotionLayout()
			return
		}

		this.boundScrollHandler = this.onScroll.bind(this)
		this.boundResizeHandler = this.onResize.bind(this)

		window.addEventListener('scroll', this.boundScrollHandler, { passive: true })
		window.addEventListener('resize', this.boundResizeHandler)

		this.observeForPreload()
		this.onScroll()
	}

	override connectedCallback() {
		super.connectedCallback()
		if (this.hasTrackedInit) return
		this.hasTrackedInit = true
		this.trackAnalyticsEvent('science_animation_init')
		if (this.frameUrls.length === 0) {
			this.trackAnalyticsEvent('science_animation_no_frames')
		}
		if (this.reducedMotion) {
			this.trackAnalyticsEvent('science_animation_static_reduced_motion', {
				reason: 'prefers_reduced_motion',
			})
		}
		this.trackSectionViewIfNeeded()
	}

	private setupKeyframeText() {
		const keyframeEls = Array.from(
			this.querySelectorAll<HTMLElement>('[data-keyframe]')
		)
		for (const kf of keyframeEls) {
			const chars: HTMLSpanElement[] = []
			const reverts: (() => void)[] = []

			// Caption + title are split per-char for the staggered opacity fade.
			// The body copy (split-right) stays as a single block. Blur was
			// dropped from both — CSS filters force a new compositor layer per
			// element that's re-rasterized on every change, which dominated the
			// scroll cost.
			const charTargets = kf.querySelectorAll<HTMLElement>(
				'.science-animation__caption, .science-animation__title'
			)
			for (const el of charTargets) {
				const split = splitText(el, { type: 'chars' })
				for (const char of split.chars) {
					char.style.willChange = 'opacity'
					char.style.opacity = '0'
					chars.push(char)
				}
				reverts.push(split.revert)
			}

			const body = kf.querySelector<HTMLElement>(
				'.science-animation__split-right'
			)
			if (body) {
				body.style.willChange = 'opacity'
				body.style.opacity = '0'
			}

			kf.style.opacity = '1'

			this.keyframeData.push({
				element: kf,
				chars,
				body,
				reverts,
				start: parseFloat(kf.dataset.start ?? '0') / 100,
				end: parseFloat(kf.dataset.end ?? '100') / 100,
				fadeIn: parseFloat(kf.dataset.fadeIn ?? '5') / 100,
				fadeOut: parseFloat(kf.dataset.fadeOut ?? '5') / 100,
				charsIdle: 0,
				bodyIdle: 0,
			})
		}
	}

	private parseFrameUrls() {
		const isMobile = window.matchMedia(
			`(max-width: ${this.mobileBreakpoint}px)`
		).matches

		const mobileScript = this.querySelector<HTMLScriptElement>(
			'script[data-science-frames-mobile]'
		)
		const desktopScript = this.querySelector<HTMLScriptElement>(
			'script[data-science-frames]'
		)
		const scriptEl = isMobile && mobileScript ? mobileScript : desktopScript
		if (!scriptEl) return
		try {
			const urls = JSON.parse(scriptEl.textContent ?? '[]')
			if (Array.isArray(urls)) {
				this.frameUrls = urls.filter((u): u is string => typeof u === 'string')
				this.frames = new Array(this.frameUrls.length).fill(null)
				this.framesLoaded = new Array(this.frameUrls.length).fill(false)
				this.framesQueued = new Array(this.frameUrls.length).fill(false)
			}
		} catch {
			/* malformed JSON — leave frames empty, poster will remain visible */
		}
	}

	private parseFrameHoldSettings() {
		this.firstFrameHold = this.parseHoldPercent(this.dataset.firstFrameHold)
		this.lastFrameHold = this.parseHoldPercent(this.dataset.lastFrameHold)
	}

	private parseHoldPercent(value?: string): number {
		const parsed = parseFloat(value ?? '0')
		if (!Number.isFinite(parsed)) return 0
		return Math.max(0, Math.min(40, parsed)) / 100
	}

	private setupCanvas() {
		if (!this.canvas) return
		const ctx = this.canvas.getContext('2d', { alpha: false })
		if (!ctx) {
			this.canvas = undefined
			return
		}
		this.ctx = ctx
		this.resizeCanvas()
	}

	private resizeCanvas() {
		if (!this.canvas) return
		this.dpr = Math.min(window.devicePixelRatio || 1, 2)
		const { clientWidth, clientHeight } = this.sticky
		const targetW = Math.round(clientWidth * this.dpr)
		const targetH = Math.round(clientHeight * this.dpr)
		if (this.canvas.width !== targetW || this.canvas.height !== targetH) {
			this.canvas.width = targetW
			this.canvas.height = targetH
		}
		if (this.animationReady && this.currentFrameIndex >= 0) {
			this.drawFrame(this.currentFrameIndex, true)
		}
	}

	private setupSkipButton() {
		if (!this.skipButton) return
		this.skipButton.addEventListener('click', () => this.skip())
	}

	private skip() {
		this.trackAnalyticsEvent('science_animation_skip', {
			scroll_direction: this.scrollDirection,
		})

		// Direction-aware skip: scrolling down -> jump past the section;
		// scrolling up -> jump back to before it. Either way, the animation
		// itself keeps playing during the scroll; we never collapse the section.
		const outerTop = this.outer.getBoundingClientRect().top + window.scrollY
		const outerBottom = outerTop + this.outer.offsetHeight - window.innerHeight
		const target = this.scrollDirection === 'up' ? outerTop : outerBottom
		window.scrollTo({ top: target, behavior: 'smooth' })

		// Move focus to a relevant landmark so keyboard / screen reader users
		// land somewhere sensible after the smooth scroll.
		const focusTarget =
			this.scrollDirection === 'up'
				? (this.previousElementSibling as HTMLElement | null)
				: (this.nextElementSibling as HTMLElement | null)
		if (focusTarget) {
			const focusable = focusTarget.querySelector<HTMLElement>(
				'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
			)
			focusable?.focus({ preventScroll: true })
		}
	}

	private observeForPreload() {
		// Start loading the full selected frame sequence before the section is visible.
		if (!('IntersectionObserver' in window)) {
			this.startFrameLoad()
			return
		}
		this.intersectionObserver = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting && !this.intersected) {
						this.intersected = true
						this.startFrameLoad()
						this.intersectionObserver?.disconnect()
					}
				}
			},
			{ rootMargin: '25% 0% 25% 0%' }
		)
		this.intersectionObserver.observe(this)
	}

	private isNearViewport() {
		const rect = this.getBoundingClientRect()
		const margin = window.innerHeight * 0.25
		return rect.top < window.innerHeight + margin && rect.bottom > -margin
	}

	private startFrameLoad() {
		if (this.frameUrls.length === 0) return
		if (this.frameLoadStarted) return
		this.frameLoadStarted = true
		this.frameLoadStartedAt = performance.now()
		this.updateLoaderProgress()

		// Anchor/current frames stay at the front, then the rest of the sequence
		// fills in behind them. The canvas is revealed as soon as a usable frame
		// is available; loading continues in the background.
		const total = this.frameUrls.length
		const anchorIndices = [
			0,
			total - 1,
			Math.floor(total / 2),
			Math.floor(total / 4),
			Math.floor((total * 3) / 4),
		]
		for (const idx of anchorIndices) this.queueFrame(idx)

		this.queueFramesAround(
			this.frameIndexForProgress(
				this.frameProgressForScrollProgress(this.targetProgress)
			),
			this.preloadRadius,
			true
		)
		for (let idx = 0; idx < total; idx++) this.queueFrame(idx)
		this.pumpFrameQueue()
	}

	private queueFramesAround(center: number, radius: number, priority = false) {
		if (center < 0) return
		const indices = [center]
		for (let offset = 1; offset <= radius; offset++) {
			indices.push(center + offset, center - offset)
		}
		if (priority) {
			for (let i = indices.length - 1; i >= 0; i--) {
				this.queueFrame(indices[i], true)
			}
		} else {
			for (const index of indices) this.queueFrame(index)
		}
		this.pumpFrameQueue()
	}

	private queueFrame(index: number, priority = false) {
		if (index < 0 || index >= this.frameUrls.length) return
		if (this.framesLoaded[index]) return
		if (this.framesQueued[index]) {
			if (priority) {
				const queuedIndex = this.loadQueue.indexOf(index)
				if (queuedIndex > -1) {
					this.loadQueue.splice(queuedIndex, 1)
					this.loadQueue.unshift(index)
				}
			}
			return
		}
		this.framesQueued[index] = true
		if (priority) this.loadQueue.unshift(index)
		else this.loadQueue.push(index)
	}

	private pumpFrameQueue() {
		while (this.inflightLoads < this.loadConcurrency && this.loadQueue.length > 0) {
			const idx = this.loadQueue.shift() as number
			if (this.framesLoaded[idx]) continue
			this.inflightLoads++
			this.loadFrame(idx).finally(() => {
				this.inflightLoads--
				this.pumpFrameQueue()
			})
		}
	}

	private loadFrame(index: number): Promise<void> {
		return new Promise((resolve) => {
			const img = new Image()
			this.frames[index] = img
			img.decoding = 'async'
			img.onload = () => {
				this.decodeFrame(img).then(() => {
					this.frames[index] = img
					this.framesLoaded[index] = true
					this.onFrameSettled()
					resolve()
				})
			}
			img.onerror = () => {
				this.frames[index] = null
				this.framesLoaded[index] = true
				if (!this.hasTrackedFrameError) {
					this.hasTrackedFrameError = true
					this.trackAnalyticsEvent('science_animation_frame_error', {
						frame_index: index + 1,
					})
				}
				this.onFrameSettled()
				resolve()
			}
			img.src = this.frameUrls[index]
		})
	}

	private decodeFrame(img: HTMLImageElement): Promise<void> {
		if (!img.decode) return Promise.resolve()
		let timeoutId: number | undefined
		const timeout = new Promise<void>((resolve) => {
			timeoutId = window.setTimeout(resolve, this.frameDecodeTimeoutMs)
		})
		return Promise.race([img.decode(), timeout])
			.catch(() => undefined)
			.then(() => {
				if (timeoutId) window.clearTimeout(timeoutId)
			})
	}

	private onFrameSettled() {
		this.loadedCount++
		this.updateLoaderProgress()
		this.revealAnimationIfPossible()
		if (this.loadedCount >= this.frameUrls.length) {
			this.onAllFramesSettled()
		}
	}

	private revealAnimationIfPossible() {
		if (this.animationReady) return
		const want = this.frameIndexForProgress(
			this.frameProgressForScrollProgress(this.targetProgress)
		)
		const initialFrame = this.nearestLoadedIndex(want)
		if (initialFrame === -1) return
		this.animationReady = true
		this.hideLoader()
		if (this.canvas) {
			this.canvas.hidden = false
		}
		if (this.poster) {
			this.poster.classList.add('science-animation__poster--hidden')
		}
		this.drawFrame(initialFrame, true)
		this.requestRender()
	}

	private onAllFramesSettled() {
		if (this.allFramesReady) return
		this.allFramesReady = true
		this.hideLoader()
		if (!this.hasTrackedReady) {
			this.hasTrackedReady = true
			this.trackAnalyticsEvent('science_animation_ready', {
				all_frames_ready_ms: Math.round(
					performance.now() -
						(this.frameLoadStartedAt ?? this.connectedAt)
				),
			})
		}
		this.revealAnimationIfPossible()
		this.requestRender()
	}

	private frameIndexForProgress(progress: number): number {
		if (this.frameUrls.length === 0) return -1
		const clamped = Math.max(0, Math.min(1, progress))
		const idx = Math.round(clamped * (this.frameUrls.length - 1))
		return Math.max(0, Math.min(this.frameUrls.length - 1, idx))
	}

	private getProgress(): number {
		const rect = this.outer.getBoundingClientRect()
		const outerHeight = this.outer.offsetHeight
		const viewportHeight = window.innerHeight
		const scrollRange = outerHeight - viewportHeight

		if (scrollRange <= 0) return 0

		const progress = -rect.top / scrollRange
		return Math.max(0, Math.min(1, progress))
	}

	private frameProgressForScrollProgress(progress: number): number {
		const activeStart = this.firstFrameHold
		const activeEnd = 1 - this.lastFrameHold
		const activeRange = activeEnd - activeStart
		if (progress <= activeStart) return 0
		if (progress >= activeEnd) return 1
		if (activeRange <= Number.EPSILON) return progress >= activeEnd ? 1 : 0
		return (progress - activeStart) / activeRange
	}

	private onScroll() {
		if (this.reducedMotion) return
		this.trackSectionViewIfNeeded()
		const currentY = window.scrollY
		if (currentY > this.lastScrollY) this.scrollDirection = 'down'
		else if (currentY < this.lastScrollY) this.scrollDirection = 'up'
		this.lastScrollY = currentY
		this.targetProgress = this.getProgress()
		if (!this.frameLoadStarted && this.isNearViewport()) {
			this.intersected = true
			this.startFrameLoad()
			this.intersectionObserver?.disconnect()
		}
		if (this.frameLoadStarted) {
			this.queueFramesAround(
				this.frameIndexForProgress(
					this.frameProgressForScrollProgress(this.targetProgress)
				),
				this.preloadRadius,
				true
			)
		}
		this.maybeShowLoader()
		this.requestRender()
	}

	private onResize() {
		this.resizeCanvas()
		this.onScroll()
	}

	private maybeShowLoader() {
		if (this.frameUrls.length === 0) return
		if (this.animationReady || this.allFramesReady || this.loaderShown) return
		if (!this.loader) return
		// Only show the overlay if the user is actually inside the section.
		const rect = this.outer.getBoundingClientRect()
		const inSection = rect.top <= 0 && rect.bottom > window.innerHeight
		if (!inSection) return
		if (this.loaderTimeoutId) return
		this.loaderTimeoutId = window.setTimeout(() => {
			if (this.allFramesReady) return
			if (this.animationReady) return
			this.loaderShown = true
			this.updateLoaderProgress()
			if (this.loader) this.loader.hidden = false
			if (!this.hasTrackedLoaderShown) {
				this.hasTrackedLoaderShown = true
				this.trackAnalyticsEvent('science_animation_loader_shown', {
					loader_delay_ms: this.loaderShowDelayMs,
				})
			}
		}, this.loaderShowDelayMs)
	}

	private updateLoaderProgress() {
		if (!this.loaderProgress || this.frameUrls.length === 0) return
		const progress = Math.max(
			0,
			Math.min(1, this.loadedCount / this.frameUrls.length)
		)
		const value = Math.round(progress * 100)
		this.loaderProgress.style.setProperty(
			'--science-animation-load-progress',
			`${progress}`
		)
		this.loaderProgress.setAttribute('aria-valuenow', `${value}`)
	}

	private hideLoader() {
		if (this.loaderTimeoutId) {
			window.clearTimeout(this.loaderTimeoutId)
			this.loaderTimeoutId = undefined
		}
		if (this.loader) this.loader.hidden = true
		this.loaderShown = false
	}

	private requestRender() {
		if (this.frameRequestId) return
		this.frameRequestId = window.requestAnimationFrame(() => this.tick())
	}

	private tick() {
		this.frameRequestId = undefined
		const delta = this.targetProgress - this.smoothedProgress

		if (Math.abs(delta) > this.smoothingEpsilon) {
			this.smoothedProgress += delta * this.smoothingFactor
		} else {
			this.smoothedProgress = this.targetProgress
		}

		this.render(this.smoothedProgress)

		if (Math.abs(this.targetProgress - this.smoothedProgress) > this.smoothingEpsilon) {
			this.requestRender()
		}
	}

	private render(progress: number) {
		this.updateCanvas(progress)
		this.updateKeyframes(progress)
	}

	private updateCanvas(progress: number) {
		if (!this.animationReady) {
			this.revealAnimationIfPossible()
			return
		}
		const frameProgress = this.frameProgressForScrollProgress(progress)
		const want = this.frameIndexForProgress(frameProgress)
		if (want === -1 || want === this.currentFrameIndex) return
		this.queueFramesAround(want, this.preloadRadius, true)
		// If the exact target frame isn't loaded yet, fall back to the nearest loaded
		// frame so we never blank out — works because anchor frames load first.
		const drawn = this.nearestLoadedIndex(want)
		if (drawn === -1) return
		this.drawFrame(drawn)
	}

	private nearestLoadedIndex(target: number): number {
		if (this.framesLoaded[target] && this.frames[target]) return target
		const max = this.framesLoaded.length - 1
		for (let offset = 1; offset <= max; offset++) {
			const lo = target - offset
			const hi = target + offset
			if (lo >= 0 && this.framesLoaded[lo] && this.frames[lo]) return lo
			if (hi <= max && this.framesLoaded[hi] && this.frames[hi]) return hi
		}
		return -1
	}

	private drawFrame(index: number, force = false) {
		if (!this.ctx || !this.canvas) return
		if (!force && index === this.currentFrameIndex) return
		const img = this.frames[index]
		if (!img) return

		const cw = this.canvas.width
		const ch = this.canvas.height
		// object-fit: cover behavior
		const imgRatio = img.naturalWidth / img.naturalHeight
		const canvasRatio = cw / ch
		let drawW: number, drawH: number, dx: number, dy: number
		if (imgRatio > canvasRatio) {
			drawH = ch
			drawW = ch * imgRatio
			dx = (cw - drawW) / 2
			dy = 0
		} else {
			drawW = cw
			drawH = cw / imgRatio
			dx = 0
			dy = (ch - drawH) / 2
		}
		this.ctx.drawImage(img, dx, dy, drawW, drawH)
		this.currentFrameIndex = index
	}

	private updateKeyframes(progress: number) {
		for (const data of this.keyframeData) {
			const { element, chars, body, start, end, fadeIn, fadeOut } = data
			const fadeInDistance = Math.max(fadeIn, Number.EPSILON)
			const fadeOutDistance = Math.max(fadeOut, Number.EPSILON)
			const fadeInEnd = start + fadeInDistance
			const fadeOutStart = end - fadeOutDistance

			if (progress < start || progress > end) {
				if (data.charsIdle !== 0) {
					this.setCharsState(chars, 0)
					data.charsIdle = 0
				}
				element.style.pointerEvents = 'none'
			} else if (progress < fadeInEnd) {
				const fadeProgress = (progress - start) / fadeInDistance
				this.animateChars(chars, fadeProgress)
				data.charsIdle = null
				element.style.pointerEvents = 'auto'
			} else if (fadeOutStart < end && progress > fadeOutStart) {
				const fadeProgress = (end - progress) / fadeOutDistance
				this.animateChars(chars, fadeProgress)
				data.charsIdle = null
				element.style.pointerEvents = 'auto'
			} else {
				if (data.charsIdle !== 1) {
					this.setCharsState(chars, 1)
					data.charsIdle = 1
				}
				element.style.pointerEvents = 'auto'
			}

			if (body) {
				// Body fades in after the title is fully in, fades out before the
				// title starts leaving. Window is inset within the title's visible
				// range by `bodyDelay` on each side.
				const bodyFadeInStart = fadeInEnd + this.bodyDelay
				const bodyFadeInEnd = bodyFadeInStart + this.bodyFadeDuration
				const bodyFadeOutEnd = fadeOutStart - this.bodyDelay
				const bodyFadeOutStart = bodyFadeOutEnd - this.bodyFadeDuration

				if (
					bodyFadeInStart >= bodyFadeOutEnd ||
					progress <= bodyFadeInStart ||
					progress >= bodyFadeOutEnd
				) {
					if (data.bodyIdle !== 0) {
						this.setBodyState(body, 0)
						data.bodyIdle = 0
					}
				} else if (progress < bodyFadeInEnd) {
					this.setBodyState(
						body,
						(progress - bodyFadeInStart) / this.bodyFadeDuration
					)
					data.bodyIdle = null
				} else if (progress < bodyFadeOutStart) {
					if (data.bodyIdle !== 1) {
						this.setBodyState(body, 1)
						data.bodyIdle = 1
					}
				} else {
					this.setBodyState(
						body,
						(bodyFadeOutEnd - progress) / this.bodyFadeDuration
					)
					data.bodyIdle = null
				}
			}
		}
	}

	private animateChars(chars: HTMLSpanElement[], fadeProgress: number) {
		const charCount = chars.length
		if (charCount === 0) return

		for (let i = 0; i < charCount; i++) {
			const charNorm = charCount > 1 ? i / (charCount - 1) : 0
			const charStart = charNorm * this.charStaggerTotal
			const charEnd = charStart + (1 - this.charStaggerTotal)
			const charProgress = Math.max(
				0,
				Math.min(1, (fadeProgress - charStart) / (charEnd - charStart))
			)
			chars[i].style.opacity = `${charProgress}`
		}
	}

	private setCharsState(chars: HTMLSpanElement[], opacity: number) {
		const value = `${opacity}`
		for (const char of chars) char.style.opacity = value
	}

	private setBodyState(body: HTMLElement, opacity: number) {
		body.style.opacity = `${opacity}`
	}

	private applyReducedMotionLayout() {
		// Reveal all keyframe text and remove the scroll-jack — used by both
		// prefers-reduced-motion users and the Skip button.
		this.classList.add('science-animation--static')
		this.hideLoader()
		if (this.canvas) this.canvas.hidden = true
		if (this.poster) {
			this.poster.classList.remove('science-animation__poster--hidden')
		}
		for (const data of this.keyframeData) {
			data.element.style.opacity = '1'
			for (const char of data.chars) char.style.opacity = '1'
			data.charsIdle = 1
			if (data.body) {
				data.body.style.opacity = '1'
				data.bodyIdle = 1
			}
		}
		if (this.skipButton) this.skipButton.hidden = true
	}

	private trackSectionViewIfNeeded() {
		if (this.hasTrackedView) return
		const rect = this.getBoundingClientRect()
		const isInViewport = rect.top < window.innerHeight && rect.bottom > 0
		if (!isInViewport) return
		this.hasTrackedView = true
		this.trackAnalyticsEvent('science_animation_view')
	}

	private trackAnalyticsEvent(
		eventName: string,
		extraParams: ScienceAnimationAnalyticsParams = {}
	) {
		if (!scienceAnimationAnalyticsAllowed()) return

		const connection = (
			navigator as Navigator & {
				connection?: { effectiveType?: string; saveData?: boolean }
			}
		).connection

		const params: ScienceAnimationAnalyticsParams = {
			section_id: this.sectionId || 'unknown',
			page_type: this.pageType || 'unknown',
			path: window.location.pathname,
			viewport: window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches
				? 'mobile'
				: 'desktop',
			frame_count: this.frameUrls.length,
			reduced_motion: this.reducedMotion,
			network_effective_type: connection?.effectiveType ?? 'unknown',
			network_save_data: connection?.saveData ?? false,
			...extraParams,
		}

		window.dataLayer = window.dataLayer || []
		window.dataLayer.push({
			event: eventName,
			...params,
		})

		window.gtag?.('event', eventName, params)
		window.clarity?.('event', eventName)
		window.clarity?.('set', 'science_animation_page_type', `${params.page_type}`)
		window.clarity?.('set', 'science_animation_section', `${params.section_id}`)
		window.clarity?.('set', 'science_animation_viewport', `${params.viewport}`)
		window.clarity?.(
			'set',
			'science_animation_network',
			`${params.network_effective_type}`
		)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		if (this.boundScrollHandler) {
			window.removeEventListener('scroll', this.boundScrollHandler)
		}
		if (this.boundResizeHandler) {
			window.removeEventListener('resize', this.boundResizeHandler)
		}
		if (this.frameRequestId) {
			window.cancelAnimationFrame(this.frameRequestId)
			this.frameRequestId = undefined
		}
		if (this.loaderTimeoutId) {
			window.clearTimeout(this.loaderTimeoutId)
			this.loaderTimeoutId = undefined
		}
		this.intersectionObserver?.disconnect()
	}
}
